import React, { useRef, useContext } from 'react'; // Add useContext
import { Link } from 'react-router-dom';
import { FaUpload, FaCogs, FaChartLine, FaRobot, FaMap, FaBalanceScale, FaTachometerAlt, FaFilePdf, FaSync } from 'react-icons/fa';
import { motion, useInView } from 'framer-motion';
import Lottie from 'lottie-react';
import ainsightAnimation from '../assets/ainsight.json';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext'; // Import AuthContext

// Animation variants for the Header
const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut', type: 'spring', stiffness: 100 },
  },
};

// Animation variants for the Hero Section
const heroVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, delay, ease: 'easeOut', type: 'spring', stiffness: 90 },
  }),
};

// Animation variants for the How It Works Section
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: 'easeOut', type: 'spring', stiffness: 90 },
  }),
};

const stepVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut', type: 'spring', stiffness: 120 },
  }),
};

// Animation variants for the Key Features Section
const featureVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.95,
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: 'easeOut',
      type: 'spring',
      stiffness: 100,
      damping: 20,
    },
  }),
};

// Hover animation for feature cards
const hoverVariants = {
  hover: {
    scale: 1.05,
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

const AIInsights = () => {
  // Access AuthContext to check if user is logged in
  const { user, loading } = useContext(AuthContext);

  // Refs for scroll-based animations
  const heroRef = useRef(null);
  const howItWorksRef = useRef(null);
  const featuresRef = useRef(null);

  // Use useInView to detect when sections are in view
  const heroInView = useInView(heroRef, { margin: '-150px' });
  const howItWorksInView = useInView(howItWorksRef, { margin: '-150px' });
  const featuresInView = useInView(featuresRef, { margin: '-150px' });

  // Don't render the button while loading to avoid flashing incorrect state
  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <div className="flex flex-col min-h-screen font-sans text-black" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Ensure body background is white to avoid black lines */}
      <style>{`body { background-color: #FFFFFF; margin: 0; }`}</style>

      {/* Header */}
      <motion.div
        initial="hidden"
        animate={heroInView ? 'visible' : 'hidden'}
        variants={headerVariants}
      >
        <Header />
      </motion.div>

      {/* Hero Section */}
      <div
        ref={heroRef}
        className="max-w-6xl mx-auto px-6 py-12 pt-20 flex flex-col md:flex-row items-center mt-20"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {/* Left Side: Text and Button */}
        <div className="md:w-1/2 md:text-left">
          <motion.h1
            className="text-3xl md:text-5xl font-bold mb-6 uppercase"
            initial="hidden"
            animate={heroInView ? 'visible' : 'hidden'}
            variants={heroVariants}
            custom={0}
          >
            Discover How A.I.<br />
            Can Transform<br />
            Your Business
          </motion.h1>

          <motion.p
            className="text-base text-gray-600 mb-10 max-w-2xl"
            initial="hidden"
            animate={heroInView ? 'visible' : 'hidden'}
            variants={heroVariants}
            custom={0.2}
          >
            Your 24/7 AI expert. We keep up with AI—so you and your business don’t have to. Get personalized reports, strategic recommendations, and clear action plans—tailored just for your business, delivered automatically.
          </motion.p>

          <motion.div
            initial="hidden"
            animate={heroInView ? 'visible' : 'hidden'}
            variants={heroVariants}
            custom={0.4}
          >
            <Link
              to={user ? "/intakeform" : "/login"} // Redirect to intakeform if logged in, else login
              className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-bold leading-6 uppercase"
            >
              Get Your Free Report
            </Link>
          </motion.div>
        </div>

        {/* Right Side: Lottie Animation */}
        <motion.div
          className="md:w-1/2 mt-8 md:mt-0 flex justify-center"
          initial="hidden"
          animate={heroInView ? 'visible' : 'hidden'}
          variants={heroVariants}
          custom={0.6}
        >
          <Lottie
            animationData={ainsightAnimation}
            loop={true}
            className="w-3/4 max-w-md"
          />
        </motion.div>
      </div>

      {/* How It Works Section */}
      <div className="py-16" style={{ backgroundColor: '#FFFFFF' }} ref={howItWorksRef}>
        <div className="max-w-5xl mx-auto px-5">
          <motion.h2
            className="text-4xl font-bold mb-4 text-[#2596be] text-center"
            initial="hidden"
            animate={howItWorksInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            How It Works
          </motion.h2>
          <motion.p
            className="text-base text-gray-900 mb-14 mt-5 text-center"
            initial="hidden"
            animate={howItWorksInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.2}
          >
            Let us simplify your A.I. adoption journey with a few easy steps.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            {/* Step 1 */}
            <motion.div
              className="flex flex-col items-center px-4"
              initial="hidden"
              animate={howItWorksInView ? 'visible' : 'hidden'}
              variants={stepVariants}
              custom={0}
            >
              <div className="bg-white p-5 rounded-full shadow-md mb-5 relative z-10">
                <FaUpload className="text-blue-600 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Data Submission</h3>
              <p className="text-gray-600 text-center">
                Provide basic company information
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              className="flex flex-col items-center px-4 relative"
              initial="hidden"
              animate={howItWorksInView ? 'visible' : 'hidden'}
              variants={stepVariants}
              custom={1}
            >
              <div className="bg-white p-5 rounded-full shadow-md mb-5 relative z-10">
                <FaCogs className="text-blue-600 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">Immediate A.I. Processing</h3>
              <p className=" text-gray-600 text-center">
                We generate personalized recommendations
              </p>
              <div className="hidden md:block absolute top-12 left-60 w-[60%] h-0.5 bg-gray-300 z-0" style={{ transform: 'translateY(50%)' }}></div>
              <div className="hidden md:block absolute top-12 right-60 w-[60%] h-0.5 bg-gray-300 z-0" style={{ transform: 'translateY(-50%)' }}></div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              className="flex flex-col items-center px-4"
              initial="hidden"
              animate={howItWorksInView ? 'visible' : 'hidden'}
              variants={stepVariants}
              custom={2}
            >
              <div className="bg-white p-5 rounded-full shadow-md mb-5 relative z-10">
                <FaChartLine className="text-blue-600 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Results Delivered</h3>
              <p className=" text-gray-600 text-center">
                Get a clear report on A.I. solutions
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="max-w-5xl mx-auto px-5 py-20 text-center" ref={featuresRef}>
        <motion.h2
          className="text-4xl font-bold mb-4 text-[#2596be]"
          initial="hidden"
          animate={featuresInView ? 'visible' : 'hidden'}
          variants={sectionVariants}
          custom={0}
        >
          Key Features
        </motion.h2>
        <motion.p
          className="text-base text-gray-900 mb-14 text-center"
          initial="hidden"
          animate={featuresInView ? 'visible' : 'hidden'}
          variants={sectionVariants}
          custom={0.2}
        >
          Unlock the full potential of A.I. with these powerful features.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature 1: Customized AI Recommendations */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={0}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <FaRobot className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Customized AI Recommendations</h3>
              <p className="text-gray-600 text-sm">
                Get specific tools and methods tailored to your industry and goals.
              </p>
            </div>
          </motion.div>

          {/* Feature 2: Step-by-Step Integration Plans */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={1}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <FaMap className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Step-by-Step Integration Plans</h3>
              <p className=" text-gray-600 text-sm">
                Receive a roadmap for how to start using AI right away.
              </p>
            </div>
          </motion.div>

          {/* Feature 3: ROI and Risk Forecasting */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={2}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <FaBalanceScale className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">ROI and Risk Forecasting</h3>
              <p className="d text-gray-600 text-sm">
                See potential gains and learn how to minimize adoption risks.
              </p>
            </div>
          </motion.div>

          {/* Feature 4: Competitor & Industry Benchmarks */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={3}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <FaTachometerAlt className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Competitor & Industry Benchmarks</h3>
              <p className="text-gray-600 text-sm">
                Discover what similar companies are doing with AI.
              </p>
            </div>
          </motion.div>

          {/* Feature 5: Instant Reports */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={4}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <FaFilePdf className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Instant Reports</h3>
              <p className=" text-gray-600 text-sm">
                Download clean, professional PDFs or view web-based summaries.
              </p>
            </div>
          </motion.div>

          {/* Feature 6: Monthly Updates */}
          <motion.div
            className="flex flex-col items-start bg-white p-5 rounded-lg shadow-md hover:shadow-lg"
            initial="hidden"
            animate={featuresInView ? 'visible' : 'hidden'}
            variants={featureVariants}
            custom={5}
            whileHover="hover"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-4 text-center">
              <FaSync className="text-blue-900 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Monthly Updates</h3>
              <p className=" text-gray-600 text-sm">
                Stay current with industry-specific insights and recommendations.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AIInsights;